/**
 * texture ของเอฟเฟกต์ (รอบ 20) — วาดเองทั้งหมดด้วย DynamicTexture (ออกแบบเอง ไม่ลอกจากเกมใด · กฎข้อ 3)
 *
 * - วงเวท: วงซ้อนสามชั้น + อักขระประดิษฐ์ (สามเหลี่ยม จุด เสี้ยวจันทร์ ขีด) เรียงรอบวง + ดาวหกแฉกกลาง
 * - จุดเรือง: วงไล่แสง ใช้กับประกาย/อนุภาค
 * - ควัน: ก้อนนุ่มขอบจาง
 * - เสาแสง: ไล่ความทึบจากโคนขึ้นยอด
 * สีขาวทั้งหมด ย้อมด้วยสีวัสดุ/อนุภาค · แคชต่อฉาก · ไม่มี canvas (เทสต์ NullEngine) คืน null แล้วผู้เรียกข้ามเอฟเฟกต์นั้น
 */
import type { Scene } from "@babylonjs/core/scene";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";

export type FxTex = "circle" | "glow" | "smoke" | "beam";

const cache = new WeakMap<Scene, Map<string, DynamicTexture>>();

export function canDrawFx(): boolean {
  return typeof document !== "undefined" || typeof OffscreenCanvas !== "undefined";
}

/**
 * texture ของเอฟเฟกต์ · `tint` = ย้อมสีลงใน texture เลย (วัสดุเรืองแสงของ Babylon ไม่ย้อม emissiveTexture ตาม emissiveColor)
 * แคชต่อ (ชนิด, สี) — สีสกิลมีจำกัด
 */
export function fxTexture(scene: Scene, kind: FxTex, tint?: string): DynamicTexture | null {
  if (!canDrawFx()) return null;
  let per = cache.get(scene);
  if (!per) { per = new Map(); cache.set(scene, per); }
  const key = `${kind}|${tint ?? ""}`;
  const hit = per.get(key);
  if (hit) return hit;
  const N = kind === "circle" ? 512 : 128;
  const tex = new DynamicTexture(`fx-${kind}`, { width: N, height: N }, scene, true);
  tex.hasAlpha = true;
  const ctx = tex.getContext() as unknown as CanvasRenderingContext2D;
  ctx.clearRect(0, 0, N, N);
  const c = N / 2;
  if (kind === "glow") {
    const g = ctx.createRadialGradient(c, c, 0, c, c, c);
    g.addColorStop(0, "rgba(255,255,255,1)");
    g.addColorStop(0.25, "rgba(255,255,255,0.8)");
    g.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, N, N);
  } else if (kind === "smoke") {
    for (let i = 0; i < 7; i++) {
      const x = c + Math.cos(i * 0.9) * N * 0.14, y = c + Math.sin(i * 1.3) * N * 0.14;
      const g = ctx.createRadialGradient(x, y, 0, x, y, N * 0.34);
      g.addColorStop(0, "rgba(255,255,255,0.55)");
      g.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, N, N);
    }
  } else if (kind === "beam") {
    // v = 0 ล่าง → 1 บน: โคนสว่าง ยอดจาง · ขอบซ้ายขวาจางให้ดูเป็นลำแสง
    const g = ctx.createLinearGradient(0, N, 0, 0);
    g.addColorStop(0, "rgba(255,255,255,1)");
    g.addColorStop(0.6, "rgba(255,255,255,0.45)");
    g.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, N, N);
  } else {
    ctx.strokeStyle = "#fff";
    ctx.fillStyle = "#fff";
    const ring = (r: number, w: number): void => {
      ctx.lineWidth = w;
      ctx.beginPath();
      ctx.arc(c, c, r, 0, Math.PI * 2);
      ctx.stroke();
    };
    ring(N * 0.47, 10);
    ring(N * 0.42, 3);
    ring(N * 0.27, 5);
    // อักขระประดิษฐ์รอบวง (ระหว่างวง 0.42 กับ 0.47 ไม่พอ วางที่ ~0.345)
    const marks = 16;
    for (let i = 0; i < marks; i++) {
      const a = (i / marks) * Math.PI * 2;
      ctx.save();
      ctx.translate(c + Math.cos(a) * N * 0.345, c + Math.sin(a) * N * 0.345);
      ctx.rotate(a + Math.PI / 2);
      ctx.lineWidth = 4;
      const s = N * 0.03;
      switch (i % 4) {
        case 0: // สามเหลี่ยมมีจุด
          ctx.beginPath(); ctx.moveTo(0, -s); ctx.lineTo(s, s); ctx.lineTo(-s, s); ctx.closePath(); ctx.stroke();
          ctx.beginPath(); ctx.arc(0, s * 0.3, s * 0.25, 0, Math.PI * 2); ctx.fill();
          break;
        case 1: // เสี้ยวจันทร์
          ctx.beginPath(); ctx.arc(0, 0, s, 0.4, Math.PI * 2 - 0.4); ctx.stroke();
          break;
        case 2: // ขีดคู่ตั้ง
          ctx.beginPath(); ctx.moveTo(-s * 0.4, -s); ctx.lineTo(-s * 0.4, s); ctx.moveTo(s * 0.4, -s); ctx.lineTo(s * 0.4, s); ctx.stroke();
          break;
        default: // ข้าวหลามตัด
          ctx.beginPath(); ctx.moveTo(0, -s); ctx.lineTo(s * 0.7, 0); ctx.lineTo(0, s); ctx.lineTo(-s * 0.7, 0); ctx.closePath(); ctx.stroke();
      }
      ctx.restore();
    }
    // ดาวหกแฉกในวงใน
    ctx.lineWidth = 4;
    for (const off of [0, Math.PI / 3]) {
      ctx.beginPath();
      for (let k = 0; k < 3; k++) {
        const a = off + (k / 3) * Math.PI * 2 - Math.PI / 2;
        const x = c + Math.cos(a) * N * 0.25, y = c + Math.sin(a) * N * 0.25;
        if (k === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.stroke();
    }
    // จุดเล็กรอบวงนอก
    for (let i = 0; i < 48; i++) {
      const a = (i / 48) * Math.PI * 2;
      ctx.beginPath();
      ctx.arc(c + Math.cos(a) * N * 0.445, c + Math.sin(a) * N * 0.445, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  if (tint) {
    // ย้อม: เก็บรูปทรง/ความทึบเดิม เปลี่ยนสีเป็นสีที่ขอ
    ctx.globalCompositeOperation = "source-in";
    ctx.globalAlpha = 1;
    ctx.fillStyle = tint;
    ctx.fillRect(0, 0, N, N);
    ctx.globalCompositeOperation = "source-over";
  }
  tex.update();
  per.set(key, tex);
  return tex;
}
